import { MapPin, Phone, Smartphone, Mail, Globe, ExternalLink, Shield, FileText, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const Impressum = () => {
  return (
    <>
      <Navigation />
      <PageBreadcrumbs items={[
        { label: "Home", path: "/" },
        { label: "Impressum" }
      ]} />
      
      {/* Wellenförmiger Hintergrund */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
      </div>

      <div className="min-h-screen relative z-10">
      
      {/* Hero Section */}
      <ResponsiveSection variant="accent" spacing="lg" containerSize="lg">
        <div className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
            Impressum
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Angaben gemäß § 5 TMG
          </p>
        </div>
      </ResponsiveSection>

      {/* Kontaktdaten Section */}
      <ResponsiveSection variant="default" spacing="md" containerSize="lg">
        <Card className="bg-card/50 border-accent/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Building2 className="h-6 w-6 text-accent" />
              Kontaktdaten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4" itemScope itemType="https://schema.org/ProfessionalService">
            <meta itemProp="name" content="Praxis Versichert - Thomas Dewein" />
            <meta itemProp="priceRange" content="$$" />
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-foreground" itemProp="name">Thomas Dewein</p>
                    <p className="text-muted-foreground">
                      <span itemProp="streetAddress">Riedgartenweg 15</span>
                    </p>
                    <p className="text-muted-foreground">
                      <span itemProp="postalCode">89275</span> <span itemProp="addressLocality">Elchingen</span>
                    </p>
                    <meta itemProp="addressCountry" content="DE" />
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-accent flex-shrink-0" />
                  <a href="tel:073168231" itemProp="telephone" className="text-muted-foreground hover:text-accent transition-colors">
                    0731 / 68231
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Smartphone className="h-5 w-5 text-accent flex-shrink-0" />
                  <a href="tel:01636389945" itemProp="telephone" className="text-muted-foreground hover:text-accent transition-colors">
                    0163 / 6389945
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-accent flex-shrink-0" />
                  <a href="mailto:info@praxis-versichert.de" itemProp="email" className="text-muted-foreground hover:text-accent transition-colors break-all">
                    info@praxis-versichert.de
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-accent flex-shrink-0" />
                  <a href="mailto:thomas.dewein@deutsches-maklerforum.de" itemProp="email" className="text-muted-foreground hover:text-accent transition-colors break-all">
                    thomas.dewein@deutsches-maklerforum.de
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </ResponsiveSection>

      {/* Berufsbezeichnung & Registrierung Section */}
      <ResponsiveSection variant="card" spacing="md" containerSize="lg">
        <Card className="bg-card/50 border-accent/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Shield className="h-6 w-6 text-accent" />
              Berufsbezeichnung & Registrierung
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <p className="text-foreground font-medium">Tätigkeiten gemäß Gewerbeordnung:</p>
              <ul className="space-y-1 text-muted-foreground ml-5 list-disc">
                <li>Versicherungsmakler gem. § 34d GewO</li>
                <li>Finanzanlagenvermittler gem. § 34f GewO</li>
                <li>Immobiliardarlehensvermittler gem. § 34i GewO</li>
              </ul>
            </div>

            <div className="space-y-3">
              <p className="text-foreground font-medium">IHK-Registrierungsnummern:</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="border-accent/30 text-accent">
                  Versicherungen: D-BEIE-KKYNN-52
                </Badge>
                <Badge variant="outline" className="border-accent/30 text-accent">
                  Finanzanlagen: D-F-155-F6HW-78
                </Badge>
                <Badge variant="outline" className="border-accent/30 text-accent">
                  Immobiliardarlehen: D-W-155-BT4W-12
                </Badge>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-foreground font-medium">Zuständige Aufsichtsbehörde:</p>
              <p className="text-muted-foreground">IHK München und Oberbayern</p>
              <p className="text-muted-foreground text-sm">
                Registrierung einsehbar bei: DIHK Deutsche Industrie- und Handelskammer
              </p>
            </div>
          </CardContent>
        </Card>
      </ResponsiveSection>

      {/* Weitere Websites Section */}
      <ResponsiveSection variant="accent" spacing="md" containerSize="lg">
        <Card className="bg-card/50 border-accent/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Globe className="h-6 w-6 text-accent" />
              Weitere Online-Angebote
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Weitere Informationen zu unseren Leistungen finden Sie auch auf folgenden Websites:
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://www.versicherungsmarket.team" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
              >
                www.versicherungsmarket.team
                <ExternalLink className="h-4 w-4" />
              </a>
              <a 
                href="https://www.praxis-versichert.de" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
              >
                www.praxis-versichert.de
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </CardContent>
        </Card>
      </ResponsiveSection>

      {/* Berufshaftpflichtversicherung Section */}
      <ResponsiveSection variant="default" spacing="md" containerSize="lg">
        <Card className="bg-card/50 border-accent/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Shield className="h-6 w-6 text-accent" />
              Berufshaftpflichtversicherung
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Es besteht eine Vermögensschadenhaftpflichtversicherung für die Tätigkeit als Versicherungsmakler, 
              Finanzanlagenvermittler und Immobiliardarlehensvermittler. Weitere Details zur Berufshaftpflichtversicherung 
              finden Sie in der <Link to="/erstinformation" className="text-accent hover:text-accent/80 transition-colors underline">Erstinformation</Link>.
            </p>
          </CardContent>
        </Card>
      </ResponsiveSection>

      {/* Verantwortlich für den Inhalt Section */}
      <ResponsiveSection variant="card" spacing="md" containerSize="lg">
        <Card className="bg-card/50 border-accent/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <FileText className="h-6 w-6 text-accent" />
              Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Thomas Dewein</p>
            <p className="text-muted-foreground">Riedgartenweg 15</p>
            <p className="text-muted-foreground">89275 Elchingen</p>
          </CardContent>
        </Card>
      </ResponsiveSection>

      {/* Online-Streitbeilegung Section */}
      <ResponsiveSection variant="default" spacing="md" containerSize="lg">
        <Card className="bg-card/50 border-accent/20">
          <CardHeader>
            <CardTitle className="text-2xl">
              Online-Streitbeilegung
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-muted-foreground">
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
            </p>
            <a 
              href="https://ec.europa.eu/consumers/odr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
            >
              https://ec.europa.eu/consumers/odr
              <ExternalLink className="h-4 w-4" />
            </a>
            <p className="text-muted-foreground text-sm mt-4">
              Hinweis gemäß Verbraucherstreitbeilegungsgesetz (VSBG): Wir sind nicht bereit oder verpflichtet, 
              an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </CardContent>
        </Card>
      </ResponsiveSection>

      {/* Haftungsausschluss Section */}
      <ResponsiveSection variant="accent" spacing="lg" containerSize="lg">
        <Card className="bg-card/50 border-accent/20">
          <CardHeader>
            <CardTitle className="text-2xl">
              Haftungsausschluss
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Haftung für Inhalte</h3>
              <p className="text-muted-foreground text-sm">
                Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den 
                allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht 
                verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen 
                zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
              </p>
              <p className="text-muted-foreground text-sm">
                Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen 
                Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt 
                der Kenntnis einer konkreten Rechtsverletzung möglich.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Haftung für Links</h3>
              <p className="text-muted-foreground text-sm">
                Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. 
                Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten 
                Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
              </p>
              <p className="text-muted-foreground text-sm">
                Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. 
                Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Urheberrecht</h3>
              <p className="text-muted-foreground text-sm">
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen 
                Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der 
                Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
              </p>
            </div>
          </CardContent>
        </Card>
      </ResponsiveSection>

      <Footer />
      </div>
    </>
  );
};

export default Impressum;
